// Shared loader for REAL model assets (glTF/GLB under public/models/). Until
// 2026-10 every model in the game was built procedurally from primitives;
// realistic creatures now come from licensed asset files (see assetCredits.ts).
//
// - meshopt geometry compression is supported (pure-JS decoder, no wasm files
//   to host); textures are expected as WebP/JPEG inside the GLB.
// - One fetch + parse per URL for the whole session; callers CLONE the cached
//   scene (SkeletonUtils.clone for skinned meshes) per instance and must not
//   dispose the shared geometries/materials/textures (they are tagged
//   userData.shared so the existing dispose walkers skip them).
// - Never throws to the caller's frame loop: a failed load resolves to null so
//   the procedural model stays as the fallback.
import * as THREE from "three";
import { GLTFLoader, type GLTF } from "three/examples/jsm/loaders/GLTFLoader.js";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";
import { clone as cloneSkinned } from "three/examples/jsm/utils/SkeletonUtils.js";

/** OWNER RULE (2026-11-04): "the 3D models should always be the correct one".
 *  Real model assets load on EVERY machine — performance mode (lowSpec) no
 *  longer swaps them for the procedural stand-ins; it only cuts shadows, post
 *  and texture cost. The one exception is the TV app (tiny GPU budget). Off
 *  outside a browser (the node benches census the procedural builds). Reads the
 *  TV flag without importing renderer.ts (bench stubs). */
export function realModelsEnabled(): boolean {
  if (typeof document === "undefined") return false;
  try {
    if (/NeonPurgeTV/i.test(navigator.userAgent || "")) return false;
    return localStorage.getItem("neonpurge.tvapp") !== "1";
  } catch { return true; }
}
/** How long the match load screen waits for real models before giving up on
 *  them for the round (they must never pop in mid-fight). Generous on purpose:
 *  the owner wants the right model, not a fast fallback. */
export const MODEL_LOAD_WAIT_MS = 15000;

let loader: GLTFLoader | null = null;
const cache = new Map<string, Promise<GLTF | null>>();

function getLoader(): GLTFLoader {
  if (!loader) {
    loader = new GLTFLoader();
    loader.setMeshoptDecoder(MeshoptDecoder);
  }
  return loader;
}

/** Mark every geometry/material/texture of a loaded asset as shared so
 *  per-instance dispose() walkers never free the cached originals. */
function tagShared(root: THREE.Object3D): void {
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.geometry) m.geometry.userData.shared = true;
    const mats = Array.isArray(m.material) ? m.material : m.material ? [m.material] : [];
    for (const mat of mats) {
      mat.userData.shared = true;
      for (const v of Object.values(mat)) if (v instanceof THREE.Texture) v.userData.shared = true;
    }
  });
}

/** Load (once) and cache a GLB/glTF. Resolves null on any failure. */
export function loadGltf(url: string): Promise<GLTF | null> {
  let p = cache.get(url);
  if (!p) {
    p = getLoader().loadAsync(url).then((g) => { tagShared(g.scene); return g; }).catch((err) => {
      console.error(`[gltf] failed to load ${url}:`, err);
      return null;
    });
    cache.set(url, p);
  }
  return p;
}

/** Synchronous peek: the asset if it has already finished loading. */
const ready = new Map<string, GLTF>();
export function gltfIfReady(url: string): GLTF | null {
  const hit = ready.get(url);
  if (hit) return hit;
  void loadGltf(url).then((g) => { if (g) ready.set(url, g); });
  return null;
}

/** A per-instance copy of an asset's scene. Skinned meshes get their own
 *  skeleton (SkeletonUtils.clone); geometry/materials stay shared. */
export function instantiate(g: GLTF): THREE.Object3D {
  return cloneSkinned(g.scene);
}
